/**
 * @fileoverview Composable for SignalR connection management
 * @module composables/useSignalRConnection
 *
 * Manages SignalR hub connection for real-time message streaming
 * and bot response generation in the chat window.
 *
 * @vue3migration - Phase 3F: Extracted from ChatWindow/Index.vue
 */
import { ref, type Ref } from 'vue'
import { v4 as uuid } from 'uuid'
import * as _ from 'lodash-es'
import signalRHubConnection from '@/helpers/signalRHubConnection'

/**
 * Chat message interface for SignalR messages
 */
interface ChatMessage {
  id: string
  type?: string
  timestamp: Date | string
  data: {
    content: Array<{
      text?: string
    }>
  }
}

/**
 * Store interface for SignalR operations
 */
interface SignalRStore {
  setSignalRConnection: (connectionId: string) => void
  clearUnreadMessageCount: () => void
}

/**
 * Options for useSignalRConnection composable
 */
interface UseSignalRConnectionOptions {
  /** Pinia store instance */
  store: SignalRStore
  /** Messages array ref to append streaming messages */
  messages: Ref<ChatMessage[]>
  /** Semantic service URL for SignalR hub */
  semanticServiceUrl?: string
  /** Callback to scroll to bottom after message update */
  scrollToBottom?: () => void
  /** Callback to focus textbox after message complete */
  focusTextbox?: () => void
}

/**
 * Composable for SignalR connection management
 *
 * @param options - Dependencies and configuration
 * @returns SignalR state and connection methods
 *
 * @example
 * ```ts
 * const {
 *   botIsGenerateAnswer,
 *   tempGenerateMessage,
 *   tempGenerateMessageId,
 *   initSignalRConnection,
 * } = useSignalRConnection({
 *   store: useMainStore(),
 *   messages: computed(() => store.messages),
 *   semanticServiceUrl: settings.semanticServiceUrl,
 *   scrollToBottom: () => scrollToBottom(),
 *   focusTextbox: () => textbox.value?.focus(),
 * })
 * ```
 */
export function useSignalRConnection(options: UseSignalRConnectionOptions) {
  const { store, messages, semanticServiceUrl, scrollToBottom, focusTextbox } = options

  // ====================
  // State
  // ====================

  /** Whether bot is currently generating an answer */
  const botIsGenerateAnswer = ref(false)

  /** SignalR connection ID */
  const signalRConnectionId = ref<string | null>(null)

  /** Temporary message being generated */
  const tempGenerateMessage = ref<ChatMessage | null>(null)

  /** ID of temporary message being generated */
  const tempGenerateMessageId = ref<string | null>(null)

  // ====================
  // Methods
  // ====================

  /**
   * Initialize SignalR hub connection
   * Sets up event handlers for message streaming
   *
   * @returns Promise that resolves when connection is established
   */
  async function initSignalRConnection() {
    if (!semanticServiceUrl) {
      console.warn('SignalR: No semantic service URL configured')
      return
    }

    // Set up visibility change handler
    window.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        store.clearUnreadMessageCount()
      }
    })

    const hubConnection = signalRHubConnection(semanticServiceUrl)

    // Handle start of message generation
    hubConnection.on('StartReceiveMessage', () => {
      tempGenerateMessageId.value = uuid()
      tempGenerateMessage.value = {
        data: {
          content: [
            {
              text: '',
            },
          ],
        },
        id: tempGenerateMessageId.value,
        timestamp: new Date().toISOString(),
      }
      messages.value.push(tempGenerateMessage.value)
      botIsGenerateAnswer.value = true
    })

    // Handle streaming message chunks
    hubConnection.on('ReceiveMessage', (message: string) => {
      if (message) {
        const currentMessage = _.find(
          messages.value,
          (o) => o.id === tempGenerateMessageId.value
        )
        if (currentMessage) {
          const currentMessageContent = currentMessage.data.content[0].text || ''
          const currentMessageIndex = _.findIndex(
            messages.value,
            (o) => o.id === tempGenerateMessageId.value
          )
          const newMessage = currentMessageContent.concat(message)
          currentMessage.data.content[0].text = newMessage

          messages.value[currentMessageIndex] = currentMessage
          scrollToBottom?.()
        }
      }
    })

    // Handle connection ID assignment
    hubConnection.on('GetConnectionId', (connectionId: string) => {
      signalRConnectionId.value = connectionId
      store.setSignalRConnection(connectionId)
    })

    // Handle end of message generation
    hubConnection.on('EndReceiveMessage', () => {
      botIsGenerateAnswer.value = false
      setTimeout(() => {
        focusTextbox?.()
      }, 100)
    })

    // Start connection and get connection ID
    await hubConnection.start()
    await hubConnection.invoke('GetConnectionId')
  }

  return {
    // State
    botIsGenerateAnswer,
    signalRConnectionId,
    tempGenerateMessage,
    tempGenerateMessageId,

    // Methods
    initSignalRConnection,
  }
}
